import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import {
  answerBlock
} from 'actions';

import Question from 'components/Questionnaires/Question';
import { QuestionTextField, QuestionDropDownField } from 'components/Questionnaires/fields';
import { requiredValidator, validateQuestion } from 'components/Questionnaires/validators';

export const QuestionControllingPersonEntityBlock = ({ block, onAnswer, preQualAnswers }) => {
  const dispatch = useDispatch();

  const [answer, setAnswer] = useState(block.answers ?? {});

  const [entityName, setEntityName] = useState({
    id: 'entityName',
    component: QuestionTextField,
    label: 'questionnaire.crs.cp.entityName.label',
    info: {
      instructions: 'questionnaire.crs.cp.entityName.info',
    },
    visible: true,
    answer: answer?.entityName
  });
  const [control, setControl] = useState({
    id: 'control',
    component: QuestionDropDownField,
    componentProps: {
      items: 'questionnaire.crs.cp.controls',
    },
    label: 'questionnaire.crs.cp.controls.label',
    info: {
      instructions: 'questionnaire.crs.cp.controls.info',
    },
    visible: true,
    answer: answer?.control
  });
  const [nature, setNature] = useState({
    id: 'nature',
    component: QuestionDropDownField,
    componentProps: {
      items: '',
    },
    label: '',
    info: {
      instructions: '',
    },
    visible: (bAnswers, qAnswers, pAnswers) => bAnswers.control !== undefined && bAnswers.control !== '',
    validation: {
      conditions: [
        { condition: requiredValidator, message: '' }
      ]
    },
    answer: answer?.nature
  });

  const entityNameValidation = {
    conditions: [
      { condition: requiredValidator, message: 'questionnaire.crs.cp.entityName.errorMessage' }
    ]
  };

  const controlValidation = {
    conditions: [
      { condition: requiredValidator, message: 'questionnaire.crs.cp.controls.errorMessage' }
    ]
  };

  const blockIdentifier = useMemo(() => ((block && block.id) ? `${ block.id }${ block.index !== undefined ? (`.${ block.index }`) : '' }` : undefined), [block.index]);

  useEffect(() => {
    let isValid;
    if (entityName.answer !== undefined || control.answer !== undefined || nature.answer !== undefined) {
      if ((entityName.isValid === true && !entityName.answer && control.isValid === true && !control.answer)
        || (entityName.isValid === undefined && control.isValid === true && !control.answer)) {
        isValid = true;
      } else {
        isValid = entityName.isValid === true && control.isValid === true && nature.isValid === true;
      }
    }
    if (!onAnswer || typeof onAnswer !== 'function') {
      dispatch(answerBlock(block.id, answer, isValid));
    } else {
      onAnswer(block, answer, isValid);
    }
  }, [answer]);

  useEffect(() => {
    setAnswer(block.answers ?? {});
  }, [block.answers]);

  const updateDependentQuestion = (existingQuestion, setState, required, errorMessage) => {
    const newValidation = required
      ? { validation: { conditions: [{ condition: requiredValidator, message: errorMessage }] } }
      : { validation: { conditions: undefined } };
    const newQuestion = {
      ...existingQuestion,
      isValid: undefined,
      errorMessages: undefined,
      componentProps: { ...existingQuestion.componentProps },
      ...newValidation
    };

    validateQuestion(newQuestion);
    setState(newQuestion);

    return newQuestion;
  };

  const updateEntityName = (controlAnswer) => {
    const newEntityName = updateDependentQuestion(
      entityName,
      setEntityName,
      controlAnswer || entityName.answer,
      'questionnaire.crs.cp.entityName.errorMessage'
    );

    return newEntityName;
  };

  const updateControl = (entityNameAnswer) => {
    const newControl = updateDependentQuestion(
      control,
      setControl,
      entityNameAnswer || control.answer,
      'questionnaire.crs.cp.controls.errorMessage'
    );

    return newControl;
  };

  const onQuestionChange = (q, value) => {
    switch (q.id) {
      case 'entityName': {
        const applyValidation = (value != null && value !== '') || (control.answer != null && control.answer !== '');
        const newEntityName = { ...q, answer: value, validation: applyValidation ? entityNameValidation : null };
        validateQuestion(newEntityName);
        setEntityName(newEntityName);
        const newControl = updateControl(value);
        setAnswer({
          ...answer,
          entityName: value,
          control: newControl?.answer ?? undefined
        });
        break;
      }
      case 'control': {
        const applyValidation = (value != null && value !== '') || (entityName.answer != null && entityName.answer !== '');
        const newControl = { ...q, answer: value, validation: applyValidation ? controlValidation : null };
        validateQuestion(newControl);
        setControl(newControl);
        const newEntityName = updateEntityName(value);
        setAnswer({
          ...answer,
          control: value,
          entityName: newEntityName?.answer ?? undefined,
          nature: undefined
        });

        const newNature = { ...nature, answer: '', isValid: undefined, errorMessages: undefined };
        switch (value) {
          case 'LegalPerson':
            newNature.label = 'questionnaire.crs.cp.nature.person.label';
            newNature.componentProps.items = 'questionnaire.crs.cp.natures.person';
            newNature.validation.conditions[0].message = 'questionnaire.crs.cp.nature.person.errorMessage';
            newNature.otherValue = 'OtherMeans';
            break;
          case 'Trust':
            newNature.label = 'questionnaire.crs.cp.nature.trust.label';
            newNature.componentProps.items = 'questionnaire.crs.cp.natures.trust';
            newNature.validation.conditions[0].message = 'questionnaire.crs.cp.nature.trust.errorMessage';
            newNature.otherValue = 'Other';
            break;
          case 'LegalArrangement':
            newNature.label = 'questionnaire.crs.cp.nature.arrangement.label';
            newNature.componentProps.items = 'questionnaire.crs.cp.natures.arrangement';
            newNature.validation.conditions[0].message = 'questionnaire.crs.cp.nature.arrangement.errorMessage';
            newNature.otherValue = 'Other-Equivalent';
            break;
          default:
            break;
        }
        setNature(newNature);
        break;
      }
      case 'nature': {
        const newNature = { ...q, answer: value };
        validateQuestion(newNature);
        setNature(newNature);
        setAnswer({ ...answer, nature: value });
        break;
      }
      default:
        break;
    }
  };

  useEffect(() => {
    if (block.isForceEdited === true) {
      if (!answer?.entityName) {
        onQuestionChange({ ...entityName, isForceEdited: true }, '');
      }
      if (!answer?.control) {
        onQuestionChange({ ...control, isForceEdited: true }, '');
      }
      if (!answer?.nature) {
        onQuestionChange({ ...nature, isForceEdited: true }, '');
      }
    }

    if (block.isForceEdited === undefined) {
      if (entityName.isForceEdited === true) {
        setEntityName({ ...entityName, isForceEdited: undefined });
      }
      if (control.isForceEdited === true) {
        setControl({ ...control, isForceEdited: undefined });
      }
      if (nature.isForceEdited === true) {
        setNature({ ...nature, isForceEdited: undefined });
      }
    }
  }, [block.isForceEdited]);

  return (<React.Fragment>
    <Question key={ entityName.id } blockId={ blockIdentifier } question={ entityName } { ...entityName.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />
    <Question key={ control.id } blockId={ blockIdentifier } question={ control } { ...control.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />
    { typeof nature.visible === 'function' && nature.visible(answer) && <Question key={ nature.id } blockId={ blockIdentifier } question={ nature } { ...nature.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } /> }
  </React.Fragment>);
};

QuestionControllingPersonEntityBlock.displayName = 'QuestionControllingPersonEntityBlock';

QuestionControllingPersonEntityBlock.propTypes = {
  block: PropTypes.shape().isRequired,
  onAnswer: PropTypes.func,
  preQualAnswers: PropTypes.shape()
};

QuestionControllingPersonEntityBlock.defaultProps = {
  onAnswer: null,
  preQualAnswers: null
};

export default QuestionControllingPersonEntityBlock;
